import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Badge from '@/components/common/Badge';
import Button from '@/components/common/Button';
import toast from 'react-hot-toast';
import { getPortalPayouts, setupStripeConnect, checkStripeConnect } from '@/services/affiliateApi';

export default function AffiliatePortalPayouts() {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['portal-payouts', page],
    queryFn: () => getPortalPayouts(page, 20),
  });

  const { data: stripeStatus } = useQuery({
    queryKey: ['portal-stripe-connect'],
    queryFn: checkStripeConnect,
  });

  const payouts = (data as any)?.payouts || [];
  const total = (data as any)?.total || 0;
  const totalPages = Math.ceil(total / 20);
  const connected = (stripeStatus as any)?.connected;
  const payoutsEnabled = (stripeStatus as any)?.stripe_payouts_enabled;

  const handleSetupConnect = async () => {
    try {
      const result = await setupStripeConnect({
        return_url: window.location.origin + '/portal/payouts',
        refresh_url: window.location.origin + '/portal/payouts',
      });
      if (result.url) {
        window.open(result.url, '_blank');
      }
    } catch (err) {
      toast.error('Failed to setup Stripe Connect');
    }
  };

  const handleRefreshStatus = async () => {
    await queryClient.invalidateQueries({ queryKey: ['portal-stripe-connect'] });
    toast.success('Stripe status refreshed');
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Payouts</h1>

      {/* Stripe Connect Status */}
      <Card>
        <CardHeader><h2 className="text-lg font-semibold">Stripe Connect</h2></CardHeader>
        <CardBody>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Badge variant={payoutsEnabled ? 'success' : connected ? 'warning' : 'secondary'}>
                {payoutsEnabled ? 'Payouts Enabled' : connected ? 'Setup Incomplete' : 'Not Connected'}
              </Badge>
              <p className="text-sm text-gray-500">
                {payoutsEnabled
                  ? 'Your account is ready to receive payouts. Payouts are processed weekly on Sundays when your balance is $100+.'
                  : connected
                  ? 'Complete your Stripe onboarding to receive payouts.'
                  : 'Connect your Stripe account to receive commission payouts.'}
              </p>
            </div>
            <div className="flex gap-2">
              {!payoutsEnabled && (
                <Button onClick={handleSetupConnect}>
                  {connected ? 'Complete Setup' : 'Setup Stripe Connect'}
                </Button>
              )}
              {connected && (
                <Button size="sm" variant="secondary" onClick={handleRefreshStatus}>Refresh Status</Button>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Payout History */}
      <Card>
        <CardHeader>
          <p className="text-sm text-gray-500">{total} total payouts</p>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <div className="text-gray-500 text-center py-8">Loading...</div>
          ) : !payouts.length ? (
            <div className="text-gray-500 text-center py-8">No payouts yet</div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {payouts.map((p: any) => (
                    <tr key={p.id}>
                      <td className="px-4 py-3 text-sm font-medium">${(p.amount_cents / 100).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm font-mono text-xs text-gray-500">{p.stripe_transfer_id || '—'}</td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant={
                          p.status === 'completed' ? 'success' :
                          p.status === 'failed' ? 'danger' : 'warning'
                        }>{p.status}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {p.processed_at ? new Date(p.processed_at).toLocaleDateString() : new Date(p.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 px-4">
                  <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                    <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
